import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import subscribe from 'subscribe-event';
import Metrics from 'bloko/common/metrics';
import Transition from 'react-transition-group/Transition';
import requestAnimation from 'bloko/common/requestAnimation';
import { instanceOfDOMNodeOrNull } from 'bloko/common/helpers/customPropTypes';
import cssClasses from './cssClasses';
import Defaults from './defaults';
import { createKeyboardHandler, setMetrics, getTransitionStyles, updateHighlight } from './utils';
import SuggestPickerItem from './SuggestPickerItem';
import './suggest.less';

/**
 * Компонент позволяет отображать список элементов, один из которых можно выбрать, поддерживается работа
 * с клавиатурой и мышью, в качестве элементов могут выступать любые ноды или строки.
 */
class SuggestPicker extends React.PureComponent {
    static LAYERS = cssClasses.layer;

    static propTypes = {
        /**
         * Ref на DOM-элемент относительно которого будет спозиционирован SuggestPicker,
         * и на события которого он будет реагировать.
         */
        element: instanceOfDOMNodeOrNull(),
        /** Массив элементов списка для отображения */
        items: PropTypes.array,
        /** Коллбэк, вызываемый при выборе элемента из списка */
        onItemSelect: PropTypes.func,
        /**
         * Функция, которая должна вернуть содержимое переданного в нее item, по умолчанию возвращает
         * item[Defaults.FIELD]
         */
        itemContent: PropTypes.func,
        /** Функция, которая должна вернуть key для переданного в нее item, по умолчанию возвращает item.id */
        itemKey: PropTypes.func,
        /** Положение dropdown по оси Z (устанавливает z-index). [Возможные варианты](#suggest-layers) */
        layer: PropTypes.oneOf(Object.keys(SuggestPicker.LAYERS)),
        /** После отображения саджеста автоматически выделять первый пункт. По enter будет выбран этот вариант */
        autoHighlightFirstSuggest: PropTypes.bool,
        /** Время анимации открытия/закрытия SuggestPicker */
        fadeTime: PropTypes.number,
        /** Коллбэк, вызываемый при нажатии ESC */
        onHide: PropTypes.func,
        /** Коллбэк вызываемый, при нажатии клавиши вниз, когда пикер скрыт */
        onShow: PropTypes.func,
        /** Определяет доступность и видимость компонента */
        enabled: PropTypes.bool,
    };

    static defaultProps = {
        autoHighlightFirstSuggest: Defaults.AUTO_SELECT_FIRST_SUGGEST,
        items: [],
        itemContent: (item) => item[Defaults.FIELD],
        itemKey: (item) => item.id,
        layer: Defaults.LAYER,
        enabled: true,
        fadeTime: Defaults.DROPDOWN_FADE_TIME,
    };

    highlightedItemIndex = this.props.autoHighlightFirstSuggest ? 0 : -1;
    transitionStyles = getTransitionStyles(this.props.fadeTime);
    pickerRef = React.createRef();
    elementMetrics = {};

    startCheckingElementMetrics = requestAnimation(() => {
        if (!this.props.enabled || !this.props.element) {
            return;
        }
        const currentMetrics = Metrics.getMetrics(this.props.element);
        const { left, top, width, height } = currentMetrics;
        const compareMetrics = (key) => currentMetrics[key] !== this.elementMetrics[key];

        if (Object.keys({ left, top, width, height }).some(compareMetrics)) {
            this.updatePosition();
        }

        this.startCheckingElementMetrics();
    });

    componentDidMount() {
        this.unsubscribe = {
            resize: subscribe(window, 'resize', requestAnimation(this.updatePosition)),
            keydown: subscribe(this.props.element, 'keydown', this.handleKeyDown),
        };
        this.updatePosition();
        this.startCheckingElementMetrics();
    }

    componentWillUnmount() {
        const { resize, keydown } = this.unsubscribe;
        resize && resize();
        keydown && keydown();
    }

    componentDidUpdate(prevProps) {
        this.updatePosition();
        const { items, element, fadeTime, enabled } = this.props;
        if (enabled && !prevProps.enabled) {
            this.startCheckingElementMetrics();
        }
        if (prevProps.items !== items) {
            this.highlightedItemIndex = this.props.autoHighlightFirstSuggest ? 0 : -1;
        }
        if (prevProps.fadeTime !== fadeTime) {
            this.transitionStyles = getTransitionStyles(fadeTime);
        }
        if (prevProps.element !== element) {
            this.unsubscribe.keydown && this.unsubscribe.keydown();
            this.unsubscribe.keydown = subscribe(element, 'keydown', this.handleKeyDown);
        }
    }

    handleKeyDown = (event) => {
        const { enabled, items } = this.props;
        this.handleMouseMove = this.mouseMoveHandler;
        this.keyboardHandler(!enabled || !items.length, event, this.highlightedItemIndex);
    };

    handleMouseEnter = (index) => {
        this.updateHighlight(index);
    };

    mouseMoveHandler = (index) => {
        this.handleMouseMove = undefined;
        this.updateHighlight(index);
    };

    handleMouseMove = this.mouseMoveHandler;

    handleMouseDown = (event, index) => {
        const { items } = this.props;
        event.preventDefault();
        this.selectItem(items[index]);
    };

    emitHide = () => {
        const { onHide } = this.props;
        onHide && onHide();
    };

    emitShow = () => {
        const { onShow } = this.props;
        onShow && onShow();
    };

    selectItem = (item = null) => {
        const { onItemSelect } = this.props;
        onItemSelect && onItemSelect(item);
    };

    selectHighlighted = () => {
        if (this.highlightedItemIndex === -1) {
            return;
        }
        this.selectItem(this.props.items[this.highlightedItemIndex]);
    };

    updateHighlight = (index) => {
        if (!this.pickerRef.current) {
            return;
        }
        this.highlightedItemIndex = updateHighlight(this.pickerRef.current, index);
    };

    keyboardHandler = createKeyboardHandler(this.updateHighlight, this.emitHide, this.emitShow, this.selectHighlighted);

    updatePosition = () => {
        if (!this.pickerRef || !this.pickerRef.current) {
            return;
        }
        this.elementMetrics = setMetrics(this.props.element, this.pickerRef.current);
    };

    render() {
        if (!this.props.element) {
            return null;
        }

        const { layer, fadeTime, enabled, items, itemKey, itemContent, autoHighlightFirstSuggest } = this.props;
        const { stateStyles, defaultStyle } = this.transitionStyles;

        return ReactDOM.createPortal(
            <Transition in={enabled && items.length > 0} appear mountOnEnter unmountOnExit timeout={fadeTime}>
                {(transitionState) => (
                    <div
                        ref={this.pickerRef}
                        style={{
                            ...defaultStyle,
                            ...stateStyles[transitionState],
                        }}
                        className={classNames('suggest', SuggestPicker.LAYERS[layer])}
                    >
                        <ul className="suggest__items">
                            {items.map((item, index) => (
                                <SuggestPickerItem
                                    key={itemKey(item)}
                                    index={index}
                                    highlighted={index === 0 && autoHighlightFirstSuggest}
                                    handleMouseDown={this.handleMouseDown}
                                    handleMouseEnter={this.handleMouseEnter}
                                    handleMouseMove={this.handleMouseMove}
                                >
                                    {itemContent(item)}
                                </SuggestPickerItem>
                            ))}
                        </ul>
                    </div>
                )}
            </Transition>,
            document.body
        );
    }
}

export default SuggestPicker;
